/**
 * The map itself — `docs/property-maps-v2.md` §9.2.
 *
 * **MapLibre arrives by dynamic `import()`**, so a property page that never
 * opens this tab pays nothing for it: the library and its CSS are their own
 * chunk, fetched the first time a canvas mounts. §9.5's budget is 350 kB gzip
 * for the whole editor including MapLibre, which only holds if nothing else
 * imports it at the top level.
 *
 * **It draws, it does not edit.** Shapes come in as props and go out as
 * nothing; PR 3 adds the tools on top by handing this a draft layer and
 * pointer handlers. Keeping the read path a read path is what lets the print
 * page (§9.6) and the crew app (§8) use the same component.
 *
 * Three things the options list is not arbitrary about:
 *
 * **The map never rotates or pitches.** `dragRotate`, `touchPitch` and
 * `pitchWithRotate` are all off, because an axis-aligned rectangle (§5.3) is
 * only a rectangle while north is up — and a crew reading a plan at a gate
 * wants the same picture the office drew.
 *
 * **`attributionControl: false`, and we draw our own.** The provider's terms
 * require the attribution to be visible and not overlapped (§9.1), which means
 * it has to be a thing this layout positions rather than a thing a library
 * drops wherever it likes.
 *
 * **`pixelRatio` is capped at 2.** A 3× phone renders nine times the pixels of
 * a 1× screen for no visible gain and a very visible frame rate.
 */
import { useEffect, useRef, useState } from 'react'
import type { Map as MapLibreMap, StyleSpecification } from 'maplibre-gl'
import { LAYER_PALETTE, bboxOfGeometries, type Geometry, type LayerColor, type LngLat }
  from '@rowted/shared'
import { cn } from '../../lib/cn.js'
import { tokenColour } from '../../lib/token-colour.js'
import { imagerySession, type ImagerySession, type MapBaseKind } from '../../lib/map-provider.js'
import type { MapFeature, MapLayer } from '../api.js'

/** §9.2: beyond this many DOM markers, labels only above zoom 18. */
const LABEL_BUDGET = 300

export interface MapCanvasProps {
  base: MapBaseKind
  /** The property pin, which is where the camera lands with nothing drawn. */
  pin: LngLat
  /** A saved camera, if this map has one. */
  view: { center: [number, number]; zoom: number } | null
  layers: MapLayer[]
  features: MapFeature[]
  /** Ids the eye has hidden — this screen only, never sharing (§5.3). */
  hidden?: ReadonlySet<string>
  /** Told when the imagery cannot be fetched, so the tab can offer §5.7. */
  onImageryProblem?: (problem: string) => void
  /**
   * The session, once it is in — so the canvas's own credit line can name
   * the provider and its capture date (§5.3, §9.1). It comes from here
   * rather than from a second `imagerySession()` call because the attribution
   * has to be the one the tiles on screen actually came from.
   */
  onImagery?: (session: ImagerySession) => void
  onReady?: (map: MapLibreMap) => void
  /**
   * The shape being drawn right now, dashed and over everything.
   *
   * It is a prop rather than state here because the *tool* owns it: the
   * canvas renders whatever it is handed and has no opinion about what a
   * half-drawn rectangle is.
   */
  draft?: { geometry: Geometry; color: LayerColor } | null
  /** Which shape is selected, drawn with its vertices as handles. */
  selectedId?: string | null
  /**
   * The measure tape — outlines that are not on a layer (§5.4).
   *
   * Its own overlay rather than a layer, because that is what it is: a tape
   * shape belongs to nobody, is never sent to the map endpoints, and is
   * dashed for the same reason the draft is — it is not part of the plan the
   * crews will read.
   */
  tape?: { geometry: Geometry }[]
  /** A click on a shape, or on nothing. Only wired while Select is in hand. */
  onPick?: (featureId: string | null) => void
  /** True while a drawing tool is in hand: the map must not pan under it. */
  drawing?: boolean
  /** …unless the points are being placed under a crosshair (§6.2). */
  crosshair?: boolean
  /**
   * Draw on a flat background instead of the photo.
   *
   * §5.7: with the imagery out, "layers and shapes are saved and still
   * readable". A map that answers an outage with a blank rectangle is a map
   * that looks like it lost the work, so the shapes keep their own base —
   * which is the same plain base §8 gives the crew app offline, where the
   * provider's terms forbid cached tiles.
   */
  plain?: boolean
  className?: string
}

export function MapCanvas({
  base, pin, view, layers, features, hidden, onImageryProblem, onImagery, onReady,
  draft, selectedId, tape, onPick, drawing, crosshair, plain, className,
}: MapCanvasProps) {
  const host = useRef<HTMLDivElement>(null)
  const map = useRef<MapLibreMap | null>(null)
  const [session, setSession] = useState<ImagerySession | null>(null)
  const [ready, setReady] = useState(false)

  /* The tiles first: without a style there is nothing to make a map from. */
  useEffect(() => {
    if (plain) { setSession(null); return undefined }
    let live = true
    imagerySession()
      .then((got) => { if (live) { setSession(got); onImagery?.(got) } })
      .catch((cause: unknown) => {
        if (live) {
          onImageryProblem?.(cause instanceof Error
            ? cause.message
            : 'The aerial imagery could not be loaded.')
        }
      })
    return () => { live = false }
  }, [onImageryProblem, onImagery, plain])

  /* Then the map, once, for the life of this canvas. */
  useEffect(() => {
    if ((!session && !plain) || !host.current || map.current) return
    let cancelled = false
    let made: MapLibreMap | null = null

    void (async () => {
      const maplibre = await import('maplibre-gl')
      /* The CSS comes with the chunk rather than from `index.html`, for the
         same reason the library does. */
      await import('maplibre-gl/dist/maplibre-gl.css')
      /*
       * MapLibre 6 runs its tile work in a separate module and looks for it
       * beside its own file — which, once Vite has hashed the chunk, is not
       * there, so every tile failed with "Worker failed to load". Vite bundles
       * the worker (it imports the shared half) and hands back its URL.
       */
      maplibre.setWorkerUrl((await import('maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url')).default)
      if (cancelled || !host.current) return

      made = new maplibre.Map({
        container: host.current,
        style: styleFor(session),
        center: view?.center ?? pin,
        zoom: view?.zoom ?? 19,
        maxZoom: session ? Math.min(21, session.maxZoom) : 21,
        dragRotate: false,
        touchPitch: false,
        pitchWithRotate: false,
        renderWorldCopies: false,
        attributionControl: false,
        pixelRatio: Math.min(2, window.devicePixelRatio || 1),
      })
      map.current = made
      made.on('load', () => {
        if (cancelled) return
        setReady(true)
        onReady?.(made!)
      })
    })()

    return () => {
      cancelled = true
      made?.remove()
      if (map.current === made) map.current = null
      setReady(false)
    }
    /*
     * `session` alone. The camera props are the *initial* camera: changing
     * them later moves the map (the effects below) rather than rebuilding it,
     * and a rebuild would throw away wherever somebody had panned to.
     */
  }, [session, plain])

  /* The base is a style swap, not a new map — the shapes stay put. */
  useEffect(() => {
    if (!ready || !map.current || !session) return
    map.current.getSource('imagery')
    const source = map.current.getSource('imagery')
    if (source && 'setTiles' in source) {
      (source as { setTiles: (tiles: string[]) => void }).setTiles(session.tiles[base])
    }
  }, [base, ready, session])

  /* And the shapes, whenever they change. */
  useEffect(() => {
    if (!ready || !map.current) return
    drawShapes(map.current, layers, features, hidden ?? new Set())
  }, [ready, layers, features, hidden])

  /*
   * The draft and the selection, in two sources above the layers (§9.2).
   * Separate from the layers' own because they are not a layer: a half-drawn
   * rectangle belongs to nobody until it is finished, and the selection is a
   * fact about this screen rather than about the map.
   */
  useEffect(() => {
    if (!ready || !map.current) return
    setOverlay(map.current, 'draft', draft ? [draft.geometry] : [],
      draft ? LAYER_PALETTE[draft.color] : null)
  }, [ready, draft])

  useEffect(() => {
    if (!ready || !map.current) return
    const chosen = features.find((one) => one.id === selectedId)
    setOverlay(map.current, 'chosen', chosen ? [chosen.geometry as Geometry] : [], null)
  }, [ready, selectedId, features])

  useEffect(() => {
    if (!ready || !map.current) return
    setOverlay(map.current, 'tape', (tape ?? []).map((one) => one.geometry),
      tokenColour('warn'))
  }, [ready, tape])

  /*
   * Pan is off while a tool is in hand: a drag is the shape, not the map.
   *
   * **Except under the crosshair**, where panning *is* the gesture (§6.2):
   * the sight does not move, the property moves under it, and a map that
   * would not pan would be a map with one point on it. Disabling it there
   * was the bug — the whole method is "drag the map, then tap Add point".
   */
  useEffect(() => {
    if (!ready || !map.current) return
    if (drawing && !crosshair) map.current.dragPan.disable()
    else map.current.dragPan.enable()
    map.current.getCanvas().style.cursor = drawing && !crosshair ? 'crosshair' : ''
  }, [ready, drawing, crosshair])

  /* Clicking a shape, for the Select tool. */
  useEffect(() => {
    const live = map.current
    if (!ready || !live || !onPick) return undefined
    const click = (event: { point: { x: number; y: number } }) => {
      const hit = live.queryRenderedFeatures(event.point as never)
        .find((one) => String(one.layer.id).startsWith('layer-'))
      onPick(hit ? String(hit.id ?? '') || null : null)
    }
    live.on('click', click)
    return () => { live.off('click', click) }
  }, [ready, onPick])

  /* With nothing saved and shapes to show, fit them (§9.2's camera rule). */
  useEffect(() => {
    if (!ready || !map.current || view) return
    const box = bboxOfGeometries(features.map((feature) => feature.geometry))
    if (!box) return
    map.current.fitBounds([[box[0], box[1]], [box[2], box[3]]], {
      padding: 48, duration: 0, maxZoom: 20,
    })
  }, [ready, view, features])

  return (
    <div
      ref={host}
      className={cn('h-full w-full', className)}
      /* A map with no height is a map nobody can see, and the host is the
         element MapLibre measures. */
      style={{ minHeight: 240 }}
    />
  )
}

/**
 * One raster source for the base and nothing else.
 *
 * The layers' own sources are added after `load`, because their number and
 * their colours change while the map stays — putting them in the style would
 * mean rebuilding the style, and rebuilding the style loses the camera.
 */
function styleFor(session: ImagerySession | null): StyleSpecification {
  if (!session) {
    /* The plain base: one background layer in the page's own surface colour,
       so shapes drawn over a photo are still readable without one. */
    return {
      version: 8 as const,
      sources: {},
      layers: [{
        id: 'plain',
        type: 'background' as const,
        paint: { 'background-color': tokenColour('surface-group') },
      }],
    }
  }
  return {
    version: 8 as const,
    /* No glyph or sprite server: every label is a DOM marker (§9.2), which is
       the whole reason that decision was made. */
    sources: {
      imagery: {
        type: 'raster' as const,
        tiles: session.tiles.satellite,
        tileSize: 256,
        maxzoom: Math.min(21, session.maxZoom),
      },
    },
    layers: [
      { id: 'imagery', type: 'raster' as const, source: 'imagery' },
    ],
  }
}

/**
 * A GeoJSON source per layer, drawn in `sort_order`.
 *
 * Three style layers each — fill, line, circle — because one layer holds
 * polygons, lines and points at once and MapLibre paints by geometry type. A
 * layer whose eye is off is removed rather than hidden, so the map holds
 * nothing somebody is not looking at.
 */
function drawShapes(
  map: MapLibreMap, layers: MapLayer[], features: MapFeature[], hidden: ReadonlySet<string>,
): void {
  const ordered = [...layers].sort((a, b) => a.sortOrder - b.sortOrder)
  const wanted = new Set<string>()

  for (const layer of ordered) {
    const id = `layer-${layer.id}`
    if (hidden.has(layer.id)) continue
    wanted.add(id)

    const mine = features.filter((feature) => feature.layerId === layer.id)
    const data = {
      type: 'FeatureCollection' as const,
      features: mine.map((feature) => ({
        type: 'Feature' as const,
        id: feature.id,
        geometry: feature.geometry as never,
        properties: {
          name: feature.name ?? '',
          /* A shape may override its layer's colour (§5.3's swatches). */
          color: LAYER_PALETTE[feature.color ?? layer.color],
        },
      })),
    }

    const source = map.getSource(id)
    if (source && 'setData' in source) {
      (source as { setData: (value: typeof data) => void }).setData(data)
    } else {
      map.addSource(id, { type: 'geojson', data })
      map.addLayer({
        id: `${id}-fill`,
        type: 'fill',
        source: id,
        filter: ['==', ['geometry-type'], 'Polygon'],
        paint: { 'fill-color': ['get', 'color'], 'fill-opacity': 0.18 },
      })
      map.addLayer({
        id: `${id}-line`,
        type: 'line',
        source: id,
        paint: { 'line-color': ['get', 'color'], 'line-width': 2.5 },
      })
      map.addLayer({
        id: `${id}-point`,
        type: 'circle',
        source: id,
        filter: ['==', ['geometry-type'], 'Point'],
        paint: {
          'circle-radius': 7,
          'circle-color': ['get', 'color'],
          'circle-stroke-width': 2,
          /* The page's own surface colour, read off the document: MapLibre
             paints in WebGL and cannot read a CSS variable, and a hard white
             ring would be wrong the moment somebody turns dark mode on. */
          'circle-stroke-color': tokenColour('surface'),
        },
      })
    }
  }

  /*
   * The draft and the selection back on top. MapLibre paints in the order
   * layers were added, so a layer added after them would cover the shape
   * somebody is drawing — which is only ever noticed at the moment it
   * matters.
   */
  for (const id of ['draft-fill', 'draft-line', 'draft-point',
    'chosen-fill', 'chosen-line', 'chosen-point']) {
    if (map.getLayer(id)) map.moveLayer(id)
  }

  /* Whatever is no longer wanted goes, in the order MapLibre requires: the
     style layers before the source they read. */
  for (const layer of map.getStyle().layers) {
    const owner = layer.id.replace(/-(fill|line|point)$/, '')
    if (owner.startsWith('layer-') && !wanted.has(owner)) map.removeLayer(layer.id)
  }

  for (const id of Object.keys(map.getStyle().sources)) {
    if (id.startsWith('layer-') && !wanted.has(id)) map.removeSource(id)
  }
}

/**
 * The draft and the selection: one GeoJSON source each, dashed and on top.
 *
 * Dashed, because an unfinished shape and a finished one looking alike is how
 * somebody saves half a rectangle. The selection has no fill, so what is
 * under it is still readable while it is picked.
 */
function setOverlay(
  map: MapLibreMap, id: string, geometries: Geometry[], color: string | null,
): void {
  const data = {
    type: 'FeatureCollection' as const,
    features: geometries.map((geometry) => ({
      type: 'Feature' as const, geometry: geometry as never, properties: {},
    })),
  }

  const source = map.getSource(id)
  if (source && 'setData' in source) {
    (source as { setData: (value: typeof data) => void }).setData(data)
    if (color) map.setPaintProperty(`${id}-line`, 'line-color', color)
    return
  }

  map.addSource(id, { type: 'geojson', data })
  map.addLayer({
    id: `${id}-fill`,
    type: 'fill',
    source: id,
    filter: ['==', ['geometry-type'], 'Polygon'],
    paint: { 'fill-color': color ?? tokenColour('accent'), 'fill-opacity': 0.12 },
  })
  map.addLayer({
    id: `${id}-line`,
    type: 'line',
    source: id,
    paint: {
      'line-color': color ?? tokenColour('accent'),
      'line-width': 2.5,
      'line-dasharray': [2, 1.5],
    },
  })
  map.addLayer({
    id: `${id}-point`,
    type: 'circle',
    source: id,
    paint: {
      'circle-radius': 5,
      'circle-color': tokenColour('surface'),
      'circle-stroke-width': 2,
      'circle-stroke-color': color ?? tokenColour('accent'),
    },
  })
}

/** Exported for the label rule's test — §9.2's 300-marker cap. */
export const labelsVisibleAt = (markers: number, zoom: number): boolean =>
  markers <= LABEL_BUDGET || zoom > 18
